import { createSelector } from '@reduxjs/toolkit';

import { selectPaymentMethod } from '@cashier/cashier-data-access';
import { FieldsByTypeInterface, InputTypes } from '@cashier/cashier-model';

import { componentsName } from '../constants';
import { selectActiveInputs } from './activeInputs';

const DIRECTA_24_PAYMENT_METHOD = 'payment-iq-bank-directa24';
const BANK_TRANSFER_PAYMENT_METHOD = 'payment-iq-banklocal-banktransfercan';

const numberFieldsException = (type: string) => {
  return [
    InputTypes.Amount,
    InputTypes.Cvv,
    InputTypes.CardCvc,
    InputTypes.AccountId,
  ];
};

const excludedInputTypes = (type: string) => {
  return [
    InputTypes.DateTime,
    InputTypes.Cvv,
    InputTypes.CardCvc,
    InputTypes.Number,
    InputTypes.DynamicSelect,
    InputTypes.Select,
  ].some((item) => item === type);
};

export const selectFieldsByType = createSelector(
  selectActiveInputs,
  selectPaymentMethod,
  (s, { id }): FieldsByTypeInterface[] => {
    const cardInfo = {} as { cvv?: string; date_picker?: number };

    return s.reduce((acc, input) => {
      const { type, name } = input;

      if (
        (type === InputTypes.Number || type === InputTypes.Integer) &&
        name === InputTypes.Amount
      ) {
        acc.push({ ...input, componentName: componentsName.amount_input });
      }

      if (
        (type === InputTypes.DynamicSelect || type === InputTypes.Select) &&
        name === InputTypes.Amount
      ) {
        acc.push({ ...input, componentName: componentsName.amount_select });
      }

      if (
        type === InputTypes.DynamicSelect &&
        [BANK_TRANSFER_PAYMENT_METHOD, DIRECTA_24_PAYMENT_METHOD].includes(id)
      ) {
        acc.push({ ...input, componentName: componentsName.dynamic_select });
      }

      if (type === InputTypes.Number && name !== InputTypes.Amount) {
        acc.push({ ...input, componentName: componentsName.input });
      }

      if (
        (type === InputTypes.DynamicSelect || type === InputTypes.Select) &&
        ![BANK_TRANSFER_PAYMENT_METHOD, DIRECTA_24_PAYMENT_METHOD].includes(id) &&
        name !== InputTypes.Amount
      ) {
        acc.push({ ...input, componentName: componentsName.select });
      }

      if (type === InputTypes.DateTime || name === InputTypes.ExpirationDate) {
        if (cardInfo?.cvv) {
          acc[cardInfo.cvv] = {
            ...acc[cardInfo.cvv],
            [name]: { ...input, componentName: componentsName.date_picker },
          };
        } else {
          acc.push({
            [name]: { ...input, componentName: componentsName.date_picker },
          });
          cardInfo.date_picker = acc.length - 1;
        }
      }

      if (type === InputTypes.Cvv || type === InputTypes.CardCvc) {
        if (cardInfo?.date_picker) {
          acc[cardInfo.date_picker] = {
            ...acc[cardInfo.date_picker],
            [name]: { ...input, componentName: componentsName.cvv },
          };
        } else {
          acc.push({ [name]: { ...input, componentName: componentsName.cvv } });
          cardInfo[name] = acc.length - 1;
        }
      }

      if (type === InputTypes.Number && !numberFieldsException(name)) {
        acc.push({ ...input, componentName: componentsName.input });
      }

      if (type === InputTypes.Input && name === InputTypes.FiatAmount) {
        acc.push({
          ...{
            ...input,
          },
          componentName: componentsName.amount_input,
        });
      }

      if (
        !excludedInputTypes(type) &&
        name !== InputTypes.AccountId &&
        name !== InputTypes.Amount &&
        name !== InputTypes.ExpirationDate &&
        name !== InputTypes.FiatAmount
      ) {
        acc.push({ ...input, componentName: componentsName.text_field });
      }

      return acc;
    }, []);
  },
);
